import { useLocation, useNavigate, useParams } from "react-router-dom";
import { navigationState, type NavigationIntent } from "@/core/routing/navigationPolicy";
import { useRouteOverlay } from "./useRouteOverlay";
import type { MouseEvent } from "react";

interface AppointmentPrefill {
  date?: string;
  time?: string;
  startAt?: string;
}

function appointmentSearch(clientId?: string, prefill?: AppointmentPrefill) {
  const params = new URLSearchParams();
  if (clientId) params.set("clientId", clientId);
  if (prefill?.date) params.set("date", prefill.date);
  if (prefill?.time) params.set("time", prefill.time);
  if (prefill?.startAt) params.set("startAt", prefill.startAt);
  const search = params.toString();
  return search ? `?${search}` : "";
}

export function useAppNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessId, locationId } = useParams<{
    businessId: string;
    locationId: string;
  }>();
  const basePath = `/b/${businessId}/l/${locationId}`;
  const { openOverlay } = useRouteOverlay(basePath);

  // Dashboard
  const goToDashboard = () => {
    navigate(`${basePath}`, { state: navigationState("start") });
  };
  const goToAssistant = () => {
    navigate(`${basePath}/assistant`);
  };

  // Search
  const goToSearch = (event?: MouseEvent<HTMLElement>) => {
    openOverlay(`${basePath}/search`, event?.currentTarget);
  };

  // Appointments
  const goToAppointments = () => {
    navigate(`${basePath}/appointments`, { relative: "path" });
  };
  const goToAppointmentDetails = (appointmentId: string, replace: boolean) => {
    navigate(`${basePath}/appointments/${appointmentId}`, {
      relative: "path",
      replace,
      state: navigationState(replace ? "finish" : "push"),
    });
  };
  const goToAppointmentEdit = (appointmentId: string) => {
    navigate(`${basePath}/appointments/${appointmentId}/edit`, {
      relative: "path",
    });
  };
  const goToAppointmentCharge = (appointmentId: string, intent: NavigationIntent = "push") =>
    navigate(`${basePath}/appointments/${appointmentId}/payments/new`, { replace: intent === "finish", state: navigationState(intent) });
  const goToAppointmentChargeCard = (appointmentId: string) =>
    navigate(`${basePath}/appointments/${appointmentId}/payments/new/card`, { state: navigationState("push") });
  const goToAppointmentChargeCash = (appointmentId: string) =>
    navigate(`${basePath}/appointments/${appointmentId}/payments/new/cash`, { state: navigationState("push") });
  const goToAppointmentPayment = (appointmentId: string, paymentId: string) =>
    navigate(`${basePath}/appointments/${appointmentId}/payments/${paymentId}`, { state: navigationState("push") });
  const goToAppointmentAdd = (clientId?: string, prefill?: AppointmentPrefill) => {
    const nested = !!clientId || location.pathname.startsWith(`${basePath}/appointments`);
    navigate(`${basePath}/appointments/new${appointmentSearch(clientId, prefill)}`, {
      state: navigationState(nested ? "push" : "start"),
    });
  };
  const goToClientAppointmentAdd = (clientId?: string, prefill?: AppointmentPrefill) => {
    navigate(`${basePath}/appointments/new/client${appointmentSearch(clientId, prefill)}`);
  };
  const goToAppointmentRebook = (appointmentId: string) => {
    navigate(`${basePath}/appointments/new/client?${new URLSearchParams({ copyFrom: appointmentId })}`);
  };
  const goToCalendarEventAdd = (kind: "personal" | "blocked", prefill?: AppointmentPrefill) =>
    navigate(`${basePath}/calendar-events/new/${kind}${appointmentSearch(undefined, prefill)}`);
  const goToCalendarEventDetails = (eventId: string, replace = false) =>
    navigate(`${basePath}/calendar-events/${eventId}`, { replace, state: navigationState(replace ? "finish" : "push") });
  const goToCalendarEventEdit = (eventId: string) =>
    navigate(`${basePath}/calendar-events/${eventId}/edit`);

  // Clients
  const goToClients = () => {
    navigate(`${basePath}/clients`, { relative: "path" });
  };
  const goToClientDetails = (clientId: string, intent: NavigationIntent = "push") => {
    navigate(`${basePath}/clients/${clientId}`, { state: navigationState(intent) });
  };
  const goToClientEdit = (clientId: string) => {
    navigate(`${basePath}/clients/${clientId}/edit`, { relative: "path" });
  };
  const goToClientAdd = () => {
    navigate(`${basePath}/clients/new`, { relative: "path" });
  };
  const goToClientPaymentMethodAdd = (clientId: string) => {
    navigate(`${basePath}/clients/${clientId}/payment-methods/new`, { state: navigationState("push") });
  };

  // Services
  const goToServices = () => {
    navigate(`${basePath}/services`, { relative: "path" });
  };
  const goToServiceDetails = (serviceId: string, intent: NavigationIntent = "push") => {
    navigate(`${basePath}/services/${serviceId}`, { state: navigationState(intent) });
  };
  const goToServiceEdit = (serviceId: string) => {
    navigate(`${basePath}/services/${serviceId}/edit`, { relative: "path" });
  };
  const goToServiceAdd = () => {
    navigate(`${basePath}/services/new`, { relative: "path" });
  };

  // Forms
  const goToForms = () => navigate(`${basePath}/forms`, { state: navigationState("push") });
  const goToFormDetails = (formId: string) =>
    navigate(`${basePath}/forms/${encodeURIComponent(formId)}`, { state: navigationState("push") });

  // Apps
  const goToApps = () => {
    navigate(`${basePath}/apps`, { relative: "path" });
  };
  const goToAbout = () => {
    navigate(`${basePath}/about`);
  };
  const goToProfile = () => {
    navigate(`${basePath}/profile`);
  };
  const goToWebsite = () => {
    navigate(`${basePath}/website`);
  };
  const goToStripe = () => {
    navigate(`${basePath}/stripe`, { state: navigationState("start") });
  };
  const goToPlan = () => {
    navigate(`${basePath}/plan`, { state: navigationState("start") });
  };
  const goToPlanDetails = () => {
    navigate(`${basePath}/plan`, { state: navigationState("push") });
  };
  const goToFinances = () => {
    navigate(`${basePath}/finances`, { state: navigationState("push") });
  };
  const goToConnectedApps = () => {
    navigate(`${basePath}/connected-apps`, { state: navigationState("push") });
  };

  // Availabilities
  const goToAvailabilities = () => {
    navigate(`${basePath}/availabilities`, { relative: "path" });
  };

  // Conversations
  const goToConversations = () => {
    navigate(`${basePath}/conversations`, { relative: "path" });
  };

  const goToConversation = (conversationId: string) => {
    navigate(`${basePath}/conversations/${conversationId}/messages`, {
      relative: "path",
    });
  };

  // Notifications
  const goToNotifications = () => {
    navigate(`${basePath}/notifications`, { relative: "path" });
  };

  const goToCustomerEmails = () => {
    navigate(`${basePath}/customer-emails`);
  };
  const goToAppointmentRules = () => {
    navigate(`${basePath}/appointment-rules`);
  };

  // Navigation utilities
  const navigateBack = () => {
    navigate(-1);
  };

  return {
    // Dashboard
    goToDashboard,
    goToAssistant,

    // Search
    goToSearch,

    // Appointments
    goToAppointments,
    goToAppointmentDetails,
    goToAppointmentEdit,
    goToAppointmentCharge,
    goToAppointmentChargeCard,
    goToAppointmentChargeCash,
    goToAppointmentPayment,
    goToAppointmentAdd,
    goToClientAppointmentAdd,
    goToAppointmentRebook,
    goToCalendarEventAdd,
    goToCalendarEventDetails,
    goToCalendarEventEdit,

    // Clients
    goToClients,
    goToClientDetails,
    goToClientEdit,
    goToClientAdd,
    goToClientPaymentMethodAdd,

    // Services
    goToServices,
    goToServiceDetails,
    goToServiceEdit,
    goToServiceAdd,

    // Forms
    goToForms,
    goToFormDetails,

    // Apps
    goToApps,
    goToAbout,
    goToProfile,
    goToWebsite,
    goToStripe,
    goToPlan,
    goToPlanDetails,
    goToFinances,
    goToConnectedApps,

    // Availabilities
    goToAvailabilities,

    // Conversations
    goToConversations,
    goToConversation,

    // Notifications
    goToNotifications,
    goToCustomerEmails,
    goToAppointmentRules,

    // Utilities
    navigateBack,
  };
}
